'use client';

import { Children, memo, useEffect, useMemo, useRef, useState } from 'react';

const STAGGER_MS = 125;
const MAX_THROW = 60;
const THROW_GAIN = 2.8;
const DRAG_SLOP = 4;
const KEY_STEP = 0.35;

function distribute(items, count) {
    const columnCount = Math.min(Math.max(1, count), items.length);
    return Array.from({ length: columnCount }, (_, columnIndex) =>
        items.map(
            (_, itemIndex) => items[(columnIndex + itemIndex) % items.length]
        )
    );
}

function useReducedMotion() {
    const [reduced, setReduced] = useState(false);

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setReduced(query.matches);
        onChange();
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    return reduced;
}

function usePageVisible() {
    const [visible, setVisible] = useState(
        () =>
            typeof document === 'undefined' ||
            document.visibilityState === 'visible'
    );

    useEffect(() => {
        const onChange = () =>
            setVisible(document.visibilityState === 'visible');
        document.addEventListener('visibilitychange', onChange);
        return () => document.removeEventListener('visibilitychange', onChange);
    }, []);

    return visible;
}

function LogoColumn({ items, activeIndex, delayMs, reduced }) {
    const [state, setState] = useState({
        activeIndex,
        previousIndex: null,
    });
    const [mounted, setMounted] = useState(false);

    useEffect(() => setMounted(true), []);

    useEffect(() => {
        setState((current) => {
            if (current.activeIndex === activeIndex) return current;
            return {
                activeIndex,
                previousIndex: reduced ? null : current.activeIndex,
            };
        });
    }, [activeIndex, reduced]);

    const style = { '--logo-carousel-delay': `${delayMs}ms` };

    return (
        <div
            className="relative overflow-hidden"
            data-slot="logo-carousel-column"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none invisible select-none"
            >
                {items[0]}
            </div>
            {state.previousIndex !== null && (
                <span
                    key={`exit-${state.previousIndex}`}
                    aria-hidden="true"
                    className="logo-carousel-exit absolute inset-0 flex items-center justify-center"
                    style={style}
                    onAnimationEnd={() =>
                        setState((current) =>
                            current.previousIndex === state.previousIndex
                                ? { ...current, previousIndex: null }
                                : current
                        )
                    }
                >
                    {items[state.previousIndex]}
                </span>
            )}
            <span
                key={`enter-${activeIndex}`}
                data-slot="logo-carousel-logo"
                className={`absolute inset-0 flex items-center justify-center ${
                    mounted ? 'logo-carousel-enter' : ''
                }`}
                style={style}
            >
                {items[activeIndex]}
            </span>
        </div>
    );
}

const MemoLogoColumn = memo(LogoColumn);

function Marquee({ children, speed = 0.5, label, className = '' }) {
    const rootRef = useRef(null);
    const trackRef = useRef(null);
    const firstSetRef = useRef(null);
    const [copies, setCopies] = useState(2);
    const speedRef = useRef(speed);

    speedRef.current = speed;

    useEffect(() => {
        const root = rootRef.current;
        const track = trackRef.current;
        const firstSet = firstSetRef.current;
        if (!root || !track || !firstSet) return;

        let x = 0;
        let setWidth = 0;
        let throwVelocity = 0;
        let hovering = false;
        let visible = true;
        let frame = 0;
        let lastFrame = 0;
        let drag = null;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

        const paint = () => {
            if (setWidth) {
                x =
                    ((((x + setWidth) % setWidth) + setWidth) % setWidth) -
                    setWidth;
            }
            track.style.transform = `translate3d(${x}px, 0, 0)`;
        };

        const measure = () => {
            const gap =
                Number.parseFloat(getComputedStyle(track).columnGap) || 0;
            setWidth = firstSet.offsetWidth + gap;
            if (setWidth) {
                setCopies(
                    Math.max(2, Math.ceil(root.clientWidth / setWidth) + 1)
                );
            }
            paint();
        };

        const tick = (now) => {
            frame = requestAnimationFrame(tick);
            const step = lastFrame ? (now - lastFrame) / (1000 / 60) : 1;
            lastFrame = now;
            if (!setWidth || drag || !visible) return;

            const drifting = !reduced.matches && !hovering;
            if (drifting) x -= speedRef.current * step;
            x += throwVelocity * step;
            throwVelocity *= 0.975 ** step;
            if (Math.abs(throwVelocity) < 0.01) throwVelocity = 0;
            if (drifting || throwVelocity) paint();
        };

        const onPointerDown = (event) => {
            if (event.button !== 0) return;
            drag = {
                id: event.pointerId,
                lastX: event.clientX,
                lastTime: performance.now(),
                moved: 0,
            };
            throwVelocity = 0;
        };
        const onPointerMove = (event) => {
            if (!drag || event.pointerId !== drag.id) return;
            const now = performance.now();
            const deltaX = event.clientX - drag.lastX;
            const deltaTime = now - drag.lastTime;
            x += deltaX;
            drag.moved += Math.abs(deltaX);
            if (
                drag.moved > DRAG_SLOP &&
                !root.hasPointerCapture(event.pointerId)
            ) {
                root.setPointerCapture(event.pointerId);
            }
            if (deltaTime > 0 && !reduced.matches) {
                throwVelocity = Math.max(
                    -MAX_THROW,
                    Math.min(
                        MAX_THROW,
                        (deltaX / deltaTime) * 16.67 * THROW_GAIN
                    )
                );
            }
            drag.lastX = event.clientX;
            drag.lastTime = now;
            paint();
        };
        const onPointerUp = (event) => {
            if (!drag || event.pointerId !== drag.id) return;
            if (performance.now() - drag.lastTime > 80) throwVelocity = 0;
            root.dataset.dragged = drag.moved > DRAG_SLOP ? 'true' : '';
            drag = null;
        };
        const onClick = (event) => {
            if (root.dataset.dragged !== 'true') return;
            event.preventDefault();
            event.stopPropagation();
            root.dataset.dragged = '';
        };
        const onKeyDown = (event) => {
            if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
            event.preventDefault();
            throwVelocity = 0;
            x +=
                (event.key === 'ArrowLeft' ? 1 : -1) *
                root.clientWidth *
                KEY_STEP;
            paint();
        };
        const onPointerEnter = () => {
            hovering = true;
        };
        const onPointerLeave = () => {
            hovering = false;
        };

        root.addEventListener('pointerdown', onPointerDown);
        root.addEventListener('pointermove', onPointerMove);
        root.addEventListener('pointerup', onPointerUp);
        root.addEventListener('pointercancel', onPointerUp);
        root.addEventListener('click', onClick, true);
        root.addEventListener('keydown', onKeyDown);
        root.addEventListener('pointerenter', onPointerEnter);
        root.addEventListener('pointerleave', onPointerLeave);

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(root);
        resizeObserver.observe(firstSet);
        const intersectionObserver = new IntersectionObserver(([entry]) => {
            visible = entry?.isIntersecting ?? true;
        });
        intersectionObserver.observe(root);
        measure();
        frame = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            intersectionObserver.disconnect();
            root.removeEventListener('pointerdown', onPointerDown);
            root.removeEventListener('pointermove', onPointerMove);
            root.removeEventListener('pointerup', onPointerUp);
            root.removeEventListener('pointercancel', onPointerUp);
            root.removeEventListener('click', onClick, true);
            root.removeEventListener('keydown', onKeyDown);
            root.removeEventListener('pointerenter', onPointerEnter);
            root.removeEventListener('pointerleave', onPointerLeave);
            track.style.transform = '';
        };
    }, []);

    return (
        <div
            ref={rootRef}
            data-slot="logo-carousel-marquee"
            aria-label={label}
            aria-roledescription="marquee"
            tabIndex={0}
            className={`relative w-full cursor-grab touch-pan-y select-none overflow-hidden outline-none [mask-image:linear-gradient(to_right,transparent,#000_7%,#000_93%,transparent)] focus-visible:ring-2 focus-visible:ring-current active:cursor-grabbing ${className}`}
        >
            <div
                ref={trackRef}
                className="flex w-max gap-10 will-change-transform"
            >
                {Array.from({ length: copies }, (_, copy) => (
                    <div
                        key={copy}
                        ref={copy === 0 ? firstSetRef : undefined}
                        aria-hidden={copy > 0 || undefined}
                        inert={copy > 0 ? '' : undefined}
                        className="flex shrink-0 items-center gap-10 [&_img]:pointer-events-none"
                    >
                        {children}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function LogoCarousel({
    children,
    columnCount = 4,
    direction = 'ltr',
    variant = 'columns',
    rotationDelay = 2,
    scrollSpeed = 5,
    label = 'Credentials and certifications',
    className = '',
}) {
    const items = useMemo(() => Children.toArray(children), [children]);
    const columns = useMemo(
        () => distribute(items, columnCount),
        [items, columnCount]
    );
    const containerRef = useRef(null);
    const [inView, setInView] = useState(false);
    const reduced = useReducedMotion();
    const pageVisible = usePageVisible();
    const [indices, setIndices] = useState(() => columns.map(() => 0));
    const columnsRef = useRef(columns);
    const normalizedScrollSpeed = Math.min(
        10,
        Math.max(3, Number(scrollSpeed) || 5)
    );
    const rotationInterval =
        Math.min(5, Math.max(2, Number(rotationDelay) || 2)) * 1000;

    columnsRef.current = columns;

    useEffect(() => {
        if (variant !== 'columns' || !containerRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => setInView(entry?.isIntersecting ?? false),
            { rootMargin: '100px' }
        );
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [variant]);

    useEffect(() => {
        if (variant !== 'columns' || !inView || !pageVisible || reduced) return;
        const interval = setInterval(() => {
            setIndices((current) =>
                columnsRef.current.map(
                    (column, index) =>
                        ((current[index] ?? 0) + 1) % Math.max(1, column.length)
                )
            );
        }, rotationInterval);
        return () => clearInterval(interval);
    }, [inView, pageVisible, reduced, rotationInterval, variant]);

    if (!items.length) return null;

    if (variant === 'marquee') {
        return (
            <Marquee
                speed={normalizedScrollSpeed / 10}
                label={label}
                className={className}
            >
                {items}
            </Marquee>
        );
    }

    return (
        <div
            ref={containerRef}
            data-slot="logo-carousel"
            aria-label={label}
            className={`grid ${className}`}
            style={{
                gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
            }}
        >
            {columns.map((column, index) => (
                <MemoLogoColumn
                    key={index}
                    items={column}
                    activeIndex={
                        column.length
                            ? (indices[index] ?? 0) % column.length
                            : 0
                    }
                    delayMs={
                        reduced
                            ? 0
                            : (direction === 'rtl'
                                  ? columns.length - 1 - index
                                  : index) * STAGGER_MS
                    }
                    reduced={reduced}
                />
            ))}
        </div>
    );
}
